import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Edit3, X, Send, Ticket, Trophy, Users, Mail, Settings as SettingsIcon, LayoutGrid, BookOpen, ShieldAlert, User as UserIcon, Award, ShoppingBag, PackageCheck, Home, TrendingUp, DollarSign, Truck, Library, FileText, Boxes, Star, Flag, ShoppingCart, Receipt } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Overview from "@/components/admin/Overview";
import RequestDetail, { STATUS_FLOW, statusLabel } from "@/components/admin/RequestDetail";
import OwnershipClaimsAdmin from "@/components/admin/OwnershipClaimsAdmin";
import ShopifyAdmin from "@/components/admin/ShopifyAdmin";
import RegistryAdmin from "@/components/admin/RegistryAdmin";
import FulfillmentAdmin from "@/components/admin/FulfillmentAdmin";
import HomeDashboard from "@/components/admin/HomeDashboard";
import ProcurementReport from "@/components/admin/ProcurementReport";
import PriceListEditor from "@/components/admin/PriceListEditor";
import BatchArrivals from "@/components/admin/BatchArrivals";
import SettingsNotificationsSection, {
  ToggleField as SNSToggleField,
  RecipientListField as SNSRecipientListField,
} from "@/components/admin/SettingsNotificationsSection";
import EmailsAdmin from "@/components/admin/EmailsAdmin";
import QuotesAdmin from "@/components/admin/QuotesAdmin";
import ManualOrderEntry from "@/components/admin/ManualOrderEntry";
import InventoryAdmin from "@/components/admin/InventoryAdmin";
import AdminCatalog from "@/pages/admin/AdminCatalog";
import AdminAnalyticsLinks from "@/components/admin/AdminAnalyticsLinks";

// NOTE: The legacy "Certificates" tab (CertsAdmin) has been retired from the
// admin nav — its free-form form did not enforce album_slug + HC/SC cover type
// selection and could create records that never surfaced in the public HC/SC
// registry. All new certificates must be created via the "Registry" tab
// (RegistryAdmin), which stores album_type explicitly and drives the public
// registry HC/SC grouping strictly from that stored field.
const TABS = [
  { key: "today",        label: "Today",            Icon: Home },
  { key: "dashboard",    label: "Overview",         Icon: LayoutGrid },
  { key: "registry",     label: "Certificates",     Icon: Award },
  { key: "cert_orders",  label: "Certificate Orders", Icon: Award, href: "/admin/certificate-orders" },
  { key: "collector_analytics", label: "Collector Analytics", Icon: TrendingUp, href: "/admin/collector-analytics" },
  { key: "pricing_manager", label: "Pricing Manager", Icon: DollarSign, href: "/admin/pricing" },
  // Black 1 of 1 tracker admin — dedicated page. Both the editor and
  // the community-sighting moderation queue are reached from here.
  { key: "black_1of1", label: "Black 1 of 1 Tracker", Icon: Star, href: "/admin/black-1of1" },
  { key: "black_1of1_reports", label: "Black 1 of 1 Sightings", Icon: Flag, href: "/admin/black-1of1/reports" },
  { key: "missing",      label: "Requests",         Icon: Send },
  { key: "fulfillment",  label: "Fulfillment",      Icon: PackageCheck },
  { key: "manual_order", label: "Manual Entry",     Icon: Plus },
  // Phase 7A · Purchases + Expenses — cost capture; no accounting.
  { key: "purchases",    label: "Purchases",        Icon: ShoppingCart, href: "/admin/purchases" },
  { key: "expenses",     label: "Expenses",         Icon: Receipt,      href: "/admin/expenses" },
  { key: "conversions",  label: "Inv. Conversions", Icon: Boxes,        href: "/admin/inventory-conversions" },
  // Phase 2 · Inventory — additive, isolated tab. NOT integrated with
  // Quotes / Fulfillment / customer-facing flows during this phase.
  { key: "inventory",    label: "Inventory",        Icon: Boxes },
  { key: "procurement",  label: "Procurement",      Icon: TrendingUp },
  { key: "batch_arrivals", label: "Batch Arrivals", Icon: Truck },
  { key: "price_lists",  label: "Price Lists",      Icon: DollarSign },
  { key: "events",       label: "Events",           Icon: Ticket },
  { key: "ownership",    label: "Ownership Claims", Icon: Award },
  { key: "collectors",   label: "Collectors",       Icon: Users },
  { key: "albums",       label: "Legacy Certificate Albums", Icon: BookOpen },
  { key: "catalog",      label: "Catalog Manager",  Icon: Library },
  { key: "contacts",     label: "Contacts",         Icon: Mail },
  { key: "audit",        label: "Audit Log",        Icon: ShieldAlert },
  { key: "shopify",      label: "Shopify",          Icon: ShoppingBag },
  { key: "emails",       label: "Emails",           Icon: Mail },
  { key: "settings",     label: "Settings",         Icon: SettingsIcon },
  { key: "quotes",       label: "Quotes",           Icon: FileText },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { public_id } = useParams();
  const location = useLocation();
  // Iter T · Phase 1 — unread Quote Activity indicator. Polled every 30s
  // ONLY while the admin is signed in and the tab is visible.  If the
  // endpoint fails we hide the badge silently — no toasts, no console
  // noise. Reused where the existing tab renderer sits (no separate
  // polling architecture per user directive #10).
  const [unreadQuotes, setUnreadQuotes] = useState(0);
  useEffect(() => {
    let cancelled = false;
    async function tick() {
      try {
        const r = await api.get("/admin/quotes/unread-count");
        if (!cancelled) setUnreadQuotes(Number(r.data?.unread || 0));
      } catch (_e) { /* silent */ }
    }
    tick();
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") tick();
    }, 30_000);
    // Also refresh whenever the admin returns to the tab.
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { cancelled = true; clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, []);
  const initialTab = React.useMemo(() => {
    if (public_id) return "fulfillment";
    const q = new URLSearchParams(location.search).get("tab");
    return q || "today";
  }, [public_id, location.search]);
  const [tab, setTab] = useState(initialTab);
  useEffect(() => { setTab(initialTab); }, [initialTab]);
  const [jumpToRequest, setJumpToRequest] = useState(null);

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  const setTabAndUrl = (t) => {
    setTab(t);
    // If we were deep inside a fulfillment order, route back to the queue.
    if (public_id) nav(`/admin?tab=${t}`, { replace: true });
    else nav(`/admin?tab=${t}`, { replace: true });
  };

  return (
    <div className="container-gm py-10 md:py-14" data-testid="admin-dashboard">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="kicker kicker-red">Owner Console</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Command center</h1>
          <p className="text-[var(--smoke)] text-sm mt-2">Signed in as <span className="text-[var(--ink)] font-bold">{user?.email}</span></p>
        </div>
        <button
          onClick={doLogout}
          data-testid="admin-logout"
          className="btn-ghost text-sm"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>

      <div className="mt-6">
        <AdminAnalyticsLinks />
      </div>

      <nav className="mt-8 border-2 border-[var(--ink)] rounded-2xl bg-white p-1.5 flex flex-wrap gap-1" data-testid="admin-tabs">        {TABS.map((t) => {
          const Icon = t.Icon;
          const showQuoteBadge = t.key === "quotes" && unreadQuotes > 0;
          // Tabs marked with an `href` navigate to a full page instead
          // of swapping the inline panel — used for Certificate Orders
          // which lives at /admin/certificate-orders.
          if (t.href) {
            return (
              <a
                key={t.key}
                href={t.href}
                data-testid={`tab-${t.key}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all text-[var(--ink)] hover:bg-[var(--panini-yellow-soft)]"
              >
                <Icon size={15} /> {t.label}
              </a>
            );
          }
          return (
            <button
              key={t.key}
              onClick={() => setTabAndUrl(t.key)}
              data-testid={`tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                tab === t.key
                  ? "bg-[var(--ink)] text-[var(--panini-yellow)]"
                  : "text-[var(--ink)] hover:bg-[var(--panini-yellow-soft)]"
              }`}
            >
              <Icon size={15} /> {t.label}
              {showQuoteBadge && (
                <span
                  className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                    tab === t.key ? "bg-[var(--panini-yellow)] text-[var(--ink)]" : "bg-red-600 text-white"
                  }`}
                  data-testid="quotes-tab-unread-badge"
                  aria-label={`${unreadQuotes} unread quotes`}
                >
                  {unreadQuotes > 99 ? "99+" : unreadQuotes}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="mt-8">
        {tab === "today" && <HomeDashboard
          onNavigateToFulfillment={(status, fulfillmentGroup) => {
            sessionStorage.setItem("gm.fulfillment.queue.v1", JSON.stringify({
              statusFilter: status || "all",
              fulfillmentGroup: fulfillmentGroup || "all",
            }));
            setTabAndUrl("fulfillment");
          }}
          onNavigateToTab={(t) => setTabAndUrl(t)}
        />}
        {tab === "dashboard" && <Overview onOpenRequest={(id) => { setJumpToRequest(id); setTab("missing"); }} />}
        {tab === "registry" && <RegistryAdmin />}
        {tab === "missing" && <MissingRequests jumpToId={jumpToRequest} clearJump={() => setJumpToRequest(null)} />}
        {tab === "fulfillment" && <FulfillmentAdmin />}
        {tab === "manual_order" && <ManualOrderEntry />}
        {tab === "inventory" && <InventoryAdmin />}
        {tab === "procurement" && <ProcurementReport />}
        {tab === "batch_arrivals" && <BatchArrivals />}
        {tab === "price_lists" && <PriceListEditor />}
        {tab === "events" && <EventsAdmin />}
        {tab === "ownership" && <OwnershipClaimsAdmin />}
        {tab === "contacts" && <ContactsAdmin />}
        {tab === "collectors" && <CollectorsAdmin />}
        {tab === "albums" && <AlbumsAdmin />}
        {tab === "catalog" && <AdminCatalog />}
        {tab === "audit" && <AuditLogAdmin />}
        {tab === "shopify" && <ShopifyAdmin />}
        {tab === "quotes" && <QuotesAdmin />}
        {tab === "emails" && <EmailsAdmin />}
        {tab === "settings" && <SettingsAdmin />}
      </div>
    </div>
  );
}

function MissingRequests({ jumpToId, clearJump }) {
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/admin/sticker-requests").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (jumpToId) { setOpenId(jumpToId); clearJump?.(); } }, [jumpToId, clearJump]);

  if (!rows) return <div className="text-[var(--smoke)]">Loading…</div>;
  if (rows.length === 0) return <div className="text-[var(--smoke)]">No requests yet.</div>;

  const f = filter.toLowerCase();
  const shown = f
    ? rows.filter((r) => (r.full_name || "").toLowerCase().includes(f)
        || (r.email || "").toLowerCase().includes(f)
        || (r.request_number || "").toLowerCase().includes(f)
        || (r.status || "").toLowerCase().includes(f))
    : rows;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="kicker kicker-red">All requests ({rows.length})</div>
        <input value={filter} onChange={(e) => setFilter(e.target.value)}
               placeholder="Filter by name, email, request #, status…"
               data-testid="requests-filter"
               className="gm-field max-w-xs" />
      </div>
      <div className="rounded-2xl border-2 border-[var(--ink)] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[var(--smoke)] text-xs uppercase tracking-widest bg-[var(--panini-yellow-soft)]">
            <tr>
              <th className="text-left px-4 py-3">Request #</th>
              <th className="text-left px-4 py-3">Collector</th>
              <th className="text-left px-4 py-3">State</th>
              <th className="text-left px-4 py-3">Fulfillment</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Submitted</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className="border-t border-[var(--line)] hover:bg-[var(--panini-yellow-soft)]" data-testid={`request-row-${r.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-[var(--panini-red)] font-bold">{r.request_number || r.id.slice(0,8)}</td>
                <td className="px-4 py-3 font-bold">{r.full_name}</td>
                <td className="px-4 py-3">{r.state}</td>
                <td className="px-4 py-3 text-[var(--smoke)]">{(r.fulfillment_method || "").replace(/_/g, " ")}</td>
                <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--panini-yellow)] text-[var(--ink)] font-bold">{statusLabel(r.status)}</span></td>
                <td className="px-4 py-3 text-[var(--smoke)] text-xs">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setOpenId(r.id)} data-testid={`request-open-${r.id}`}
                          className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--panini-yellow)] font-bold">Open</button>
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-[var(--smoke)]">No requests match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {openId && (
        <RequestDetail requestId={openId} onClose={() => { setOpenId(null); load(); }} onSaved={load} />
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="kicker kicker-black text-[10px]">{label}</div>
      <div className="text-[var(--ink)] mt-1">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    new:        "bg-[var(--panini-yellow)] text-[var(--ink)]",
    reviewing:  "bg-white text-[var(--ink)] border border-[var(--line)]",
    quoted:     "bg-[var(--field-green-soft)] text-[var(--field-green-dark)]",
    invoiced:   "bg-[var(--field-green)] text-white",
    fulfilled:  "bg-[var(--ink)] text-[var(--panini-yellow)]",
    closed:     "bg-[var(--line)] text-[var(--smoke)]",
  };
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${map[status] || ""}`}>{status}</span>;
}

// ---------- Events Admin ----------
function EventsAdmin() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [rsvps, setRsvps] = useState(null);
  const [viewingRsvpsFor, setViewingRsvpsFor] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/admin/events").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this event and all its RSVPs?")) return;
    await api.delete(`/admin/events/${id}`);
    load();
  };
  const viewRsvps = async (ev) => {
    setViewingRsvpsFor(ev);
    const r = await api.get("/admin/rsvps", { params: { event_id: ev.id } });
    setRsvps(r.data);
  };
  const markPast = async (ev) => {
    setBusyId(ev.id);
    try {
      await api.post(`/admin/events/${ev.id}/mark-past`);
      toast.success(`"${ev.title}" moved to Past Events`);
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusyId(null); }
  };
  const markUpcoming = async (ev) => {
    setBusyId(ev.id);
    try {
      await api.post(`/admin/events/${ev.id}/mark-upcoming`);
      toast.success(`"${ev.title}" moved back to Upcoming`);
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusyId(null); }
  };

  // Same classification the public page uses — admin override wins,
  // date is the fallback. Kept in sync with `_classify_upcoming_or_past`
  // in backend/routers/public.py.
  const nowIso = new Date().toISOString();
  const isPastRow = (e) => {
    const st = (e.status || "").toLowerCase();
    if (st === "completed") return true;
    if (st === "published") return false;
    return Boolean(e.starts_at && e.starts_at < nowIso);
  };

  if (!rows) return <div className="text-[var(--smoke)]">Loading…</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="kicker kicker-red">All events</div>
        <button onClick={() => setEditing({})} data-testid="event-new" className="btn-red text-sm py-2 px-4">
          <Plus size={14} /> New event
        </button>
      </div>
      <div className="rounded-2xl border-2 border-[var(--ink)] bg-white overflow-hidden">
        {rows.length === 0 && <div className="p-6 text-[var(--smoke)]">No events yet.</div>}
        {rows.map((e) => {
          const past = isPastRow(e);
          return (
            <div key={e.id} className="grid grid-cols-12 gap-4 items-center px-5 py-4 border-b border-[var(--line)] text-sm" data-testid={`event-row-${e.id}`}>
              <div className="col-span-12 md:col-span-3 font-display text-xl flex items-center gap-2">
                {e.title}
                <span
                  data-testid={`event-badge-${e.id}`}
                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                    past
                      ? "bg-[var(--ink)] text-white"
                      : "bg-[var(--panini-yellow)] text-[var(--ink)]"
                  }`}
                >
                  {past ? "Past" : "Upcoming"}
                </span>
              </div>
              <div className="col-span-6 md:col-span-3 text-[var(--smoke)]">{new Date(e.starts_at).toLocaleString()}</div>
              <div className="col-span-6 md:col-span-2 text-[var(--smoke)]">{e.location}</div>
              <div className="col-span-6 md:col-span-1 font-bold">{e.rsvp_count}{e.capacity ? `/${e.capacity}` : ""}</div>
              <div className="col-span-6 md:col-span-3 flex gap-2 justify-end flex-wrap">
                {past ? (
                  <button
                    onClick={() => markUpcoming(e)}
                    disabled={busyId === e.id}
                    data-testid={`event-mark-upcoming-${e.id}`}
                    className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--panini-yellow)] hover:bg-[var(--panini-yellow)] disabled:opacity-50"
                    title="Restore to Upcoming Events"
                  >
                    Mark as Upcoming
                  </button>
                ) : (
                  <button
                    onClick={() => markPast(e)}
                    disabled={busyId === e.id}
                    data-testid={`event-mark-past-${e.id}`}
                    className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--ink)] hover:bg-[var(--ink)] hover:text-white disabled:opacity-50"
                    title="Move to Past Events (details/photos preserved)"
                  >
                    Mark as Past
                  </button>
                )}
                <button onClick={() => viewRsvps(e)} className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--line)] hover:border-[var(--ink)]" data-testid={`event-rsvps-${e.id}`}>RSVPs</button>
                <button onClick={() => setEditing(e)} className="text-xs px-2 py-1.5 rounded-full border-2 border-[var(--line)] hover:border-[var(--ink)]" data-testid={`event-edit-${e.id}`}><Edit3 size={12} /></button>
                <button onClick={() => del(e.id)} className="text-xs px-2 py-1.5 rounded-full border-2 border-[var(--line)] hover:border-[var(--panini-red)] text-[var(--panini-red)]" data-testid={`event-del-${e.id}`}><Trash2 size={12} /></button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <EventForm
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {viewingRsvpsFor && (
        <Modal onClose={() => { setViewingRsvpsFor(null); setRsvps(null); }} title={`RSVPs — ${viewingRsvpsFor.title}`}>
          {!rsvps ? <div className="text-[var(--smoke)]">Loading…</div> :
            rsvps.length === 0 ? <div className="text-[var(--smoke)]">No RSVPs yet.</div> :
            <table className="w-full text-sm">
              <thead className="text-[var(--smoke)] text-xs uppercase tracking-widest">
                <tr><th className="text-left py-2">Name</th><th className="text-left">Email</th><th>Phone</th><th>Guests</th><th>When</th></tr>
              </thead>
              <tbody>
                {rsvps.map((r) => (
                  <tr key={r.id} className="border-t border-[var(--line)]"><td className="py-2">{r.full_name}</td><td>{r.email}</td><td>{r.phone || "—"}</td><td className="text-center">{r.guests}</td><td className="text-[var(--smoke)]">{new Date(r.created_at).toLocaleString()}</td></tr>
                ))}
              </tbody>
            </table>
          }
        </Modal>
      )}
    </div>
  );
}

function EventForm({ initial, onClose, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState({
    title: initial?.title || "",
    description: initial?.description || "",
    location: initial?.location || "",
    starts_at: initial?.starts_at ? toLocalInput(initial.starts_at) : "",
    ends_at: initial?.ends_at ? toLocalInput(initial.ends_at) : "",
    capacity: initial?.capacity || "",
    published: initial?.published ?? true,
    cover_image_url: initial?.cover_image_url || "",
    // "upcoming"|"past" — resolved from server `status` if present,
    // else defaults to "upcoming" for a fresh event.
    section:
      (initial?.status || "").toLowerCase() === "completed"
        ? "past"
        : "upcoming",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        capacity: form.capacity ? Number(form.capacity) : null,
        // Persist the section as the canonical `status` field the
        // backend already understands (published/completed).
        status: form.section === "past" ? "completed" : "published",
      };
      delete payload.section;
      if (isEdit) await api.patch(`/admin/events/${initial.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success(isEdit ? "Event updated" : "Event created");
      onSaved();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title={isEdit ? "Edit event" : "New event"}>
      <form onSubmit={submit} className="space-y-4" data-testid="event-form">
        <TextField label="Title" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="ef-title" />
        <TextField label="Location" required value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="ef-location" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Starts" required type="datetime-local" value={form.starts_at} onChange={(v) => setForm({ ...form, starts_at: v })} testid="ef-starts" />
          <TextField label="Ends (optional)" type="datetime-local" value={form.ends_at} onChange={(v) => setForm({ ...form, ends_at: v })} testid="ef-ends" />
          <TextField label="Capacity (optional)" type="number" value={form.capacity} onChange={(v) => setForm({ ...form, capacity: v })} testid="ef-capacity" />
          <TextField label="Cover image URL (optional)" value={form.cover_image_url} onChange={(v) => setForm({ ...form, cover_image_url: v })} testid="ef-cover" />
        </div>
        <div>
          <div className="kicker kicker-black text-[11px] mb-2">Status</div>
          <select
            data-testid="ef-section"
            value={form.section}
            onChange={(e) => setForm({ ...form, section: e.target.value })}
            className="gm-field w-full"
          >
            <option value="upcoming">Upcoming</option>
            <option value="past">Past</option>
          </select>
          <p className="text-[11px] text-[var(--smoke)] mt-1">
            &quot;Past&quot; keeps the event live on the site but moves it into
            the Past Events section.
          </p>
        </div>
        <div>
          <div className="kicker kicker-black text-[11px] mb-2">Description</div>
          <textarea rows={5} value={form.description} required onChange={(e) => setForm({ ...form, description: e.target.value })} className="gm-field" data-testid="ef-desc" />
        </div>
        <label className="flex items-center gap-3 text-sm text-[var(--ink)]">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
          Published (visible on public Events page)
        </label>
        <button type="submit" disabled={busy} data-testid="ef-submit" className="btn-red text-sm disabled:opacity-60">
          {busy ? "Saving…" : "Save event"}
        </button>
      </form>
    </Modal>
  );
}

// ---------- Certificates Admin ----------
function CertsAdmin() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [claimCodePayload, setClaimCodePayload] = useState(null);  // one-time reveal
  const [regenConfirm, setRegenConfirm] = useState(null);          // { cert_number }
  const [certTypes, setCertTypes] = useState([]);

  const load = () => api.get("/admin/certificates").then((r) => setRows(r.data));
  const loadTypes = () => api.get("/admin/certificate-types").then((r) => setCertTypes(r.data));
  useEffect(() => { load(); loadTypes(); }, []);

  const del = async (num) => {
    if (!window.confirm(`Delete certificate ${num}?`)) return;
    await api.delete(`/admin/certificates/${num}`);
    load();
  };

  const runRegen = async (num) => {
    try {
      const r = await api.post(`/admin/certificates/${num}/regenerate-claim-code`);
      setRegenConfirm(null);
      setClaimCodePayload(r.data);
      load();
    } catch (e2) {
      const status = e2.response?.status;
      const detail = e2.response?.data?.detail;
      if (status === 409) {
        // Informative: ownership must be revoked first.
        toast.error(typeof detail === "string" ? detail :
          "This certificate is currently owned. Revoke ownership before regenerating the claim code.");
      } else if (status === 401 || status === 403) {
        toast.error("Access denied.");
      } else {
        toast.error("Could not regenerate the claim code. Please try again.");
      }
      setRegenConfirm(null);
    }
  };

  if (!rows) return <div className="text-[var(--smoke)]">Loading…</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="kicker kicker-red">All certificates</div>
        <button onClick={() => setEditing({})} data-testid="cert-new" className="btn-red text-sm py-2 px-4">
          <Plus size={14} /> New certificate
        </button>
      </div>
      <div className="rounded-2xl border-2 border-[var(--ink)] bg-white overflow-hidden">
        {rows.length === 0 && <div className="p-6 text-[var(--smoke)]">No certificates yet.</div>}
        {rows.map((c) => {
          const isOwned = Boolean(c.owner_collector_id);
          return (
          <div key={c.cert_number} className="grid grid-cols-12 gap-4 items-center px-5 py-4 border-b border-[var(--line)] text-sm" data-testid={`cert-row-${c.cert_number}`}>
            <div className="col-span-12 md:col-span-3 font-mono text-[var(--panini-red)] font-bold flex items-center gap-2">
              {c.cert_number}
              {isOwned && (
                <span
                  className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[var(--panini-yellow)] text-[var(--ink)] font-bold"
                  data-testid={`cert-owned-badge-${c.cert_number}`}
                  title={`Owned by ${c.owner_public_collector_id || "collector"}`}
                >Owned</span>
              )}
            </div>
            <div className="col-span-12 md:col-span-3 font-display text-xl">{c.collector_full_name}</div>
            <div className="col-span-6 md:col-span-2 text-[var(--smoke)] truncate">{c.album}</div>
            <div className="col-span-3 md:col-span-1 text-[var(--smoke)]">{c.completion_year || ""}</div>
            <div className="col-span-3 md:col-span-3 flex gap-2 justify-end">
              <button
                onClick={() => setRegenConfirm({ cert_number: c.cert_number })}
                disabled={isOwned}
                className={`text-[10px] uppercase tracking-widest px-2 py-1.5 rounded-full border-2 font-bold ${
                  isOwned
                    ? "border-[var(--line)] text-[var(--smoke)] opacity-50 cursor-not-allowed"
                    : "border-[var(--line)] hover:border-[var(--panini-yellow)] text-[var(--smoke)] hover:text-[var(--ink)]"
                }`}
                data-testid={`cert-regen-claim-${c.cert_number}`}
                title={isOwned
                  ? "This certificate is currently owned. Revoke ownership first in the Ownership Claims tab."
                  : "Regenerate the claim code — the previous code will be invalidated."}
              >
                Regen code
              </button>
              <button onClick={() => setEditing(c)} className="text-xs px-2 py-1.5 rounded-full border-2 border-[var(--line)] hover:border-[var(--ink)]" data-testid={`cert-edit-${c.cert_number}`}><Edit3 size={12} /></button>
              <button onClick={() => del(c.cert_number)} className="text-xs px-2 py-1.5 rounded-full border-2 border-[var(--line)] hover:border-[var(--panini-red)] text-[var(--panini-red)]" data-testid={`cert-del-${c.cert_number}`}><Trash2 size={12} /></button>
            </div>
          </div>
        );})}
      </div>

      {editing && (
        <CertForm
          initial={editing}
          certTypes={certTypes}
          onClose={() => setEditing(null)}
          onSaved={(payload) => {
            setEditing(null);
            load();
            if (payload && payload.claim_code_plaintext_once) {
              setClaimCodePayload(payload);
            }
          }}
        />
      )}
      {regenConfirm && (
        <RegenerateConfirmModal
          certNumber={regenConfirm.cert_number}
          onCancel={() => setRegenConfirm(null)}
          onConfirm={() => runRegen(regenConfirm.cert_number)}
        />
      )}
      {claimCodePayload && (
        <ClaimCodeRevealModal payload={claimCodePayload} onClose={() => setClaimCodePayload(null)} />
      )}
    </div>
  );
}

function RegenerateConfirmModal({ certNumber, onCancel, onConfirm }) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    if (!acknowledged || busy) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onCancel} title={`Regenerate claim code · ${certNumber}`}>
      <div className="space-y-4" data-testid="regen-confirm-modal">
        <div className="rounded-xl border-2 border-[var(--panini-red)] bg-[var(--panini-red)]/5 p-4">
          <div className="font-bold text-[var(--panini-red)] uppercase tracking-widest text-xs">
            ⚠ This is a destructive action
          </div>
          <ul className="mt-3 space-y-2 text-sm text-[var(--ink)] list-disc pl-5" data-testid="regen-confirm-explainer">
            <li>A completely new one-time claim code will be generated for
                <span className="font-mono font-bold text-[var(--panini-red)]"> {certNumber}</span>.</li>
            <li>Any currently active, unused code for this certificate will
                become invalid immediately.</li>
            <li>Previously <strong>consumed</strong> codes remain permanently
                unusable — regeneration never brings an old code back.</li>
            <li>The new plaintext code will be displayed <strong>only once</strong>.
                Copy or deliver it to the collector right away.</li>
          </ul>
        </div>

        <label className="flex items-start gap-3 text-sm text-[var(--ink)] cursor-pointer">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            data-testid="regen-confirm-ack"
            className="mt-1"
          />
          <span>
            I understand the previous code will be invalidated and the new code
            can only be shown once.
          </span>
        </label>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={confirm}
            disabled={!acknowledged || busy}
            data-testid="regen-confirm-submit"
            className="btn-red text-sm flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? "Generating…" : "Generate new code"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            data-testid="regen-confirm-cancel"
            className="btn-ghost text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ClaimCodeRevealModal({ payload, onClose }) {
  const code = payload.claim_code_plaintext_once;
  const num = payload.cert_number;
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard denied — user must copy manually */ }
  };

  return (
    <Modal onClose={onClose} title={`Claim code for ${num}`}>
      <div className="space-y-4" data-testid="claim-code-reveal">
        <div className="rounded-xl border-2 border-[var(--panini-red)] bg-[var(--panini-red)]/5 p-4 text-sm">
          <div className="font-bold text-[var(--panini-red)] uppercase tracking-widest text-xs">
            ⚠ One-time reveal
          </div>
          <p className="mt-2 text-[var(--ink)] leading-relaxed">
            {payload.claim_code_warning ||
              "This code will not be shown again. Copy it now and store it securely."}
          </p>
        </div>
        <div className="rounded-xl border-2 border-[var(--ink)] bg-[var(--ink)] text-[var(--panini-yellow)] p-6 font-mono text-2xl text-center tracking-widest select-all"
             data-testid="claim-code-value">
          {code}
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={copy}
            className="btn-yellow text-sm flex-1"
            data-testid="claim-code-copy"
          >
            {copied ? "Copied ✓" : "Copy to clipboard"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost text-sm"
            data-testid="claim-code-close"
          >
            I&apos;ve saved it
          </button>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)]">
          Certificate number: <span className="font-mono">{num}</span>
        </div>
      </div>
    </Modal>
  );
}

function CertForm({ initial, certTypes, onClose, onSaved }) {
  const isEdit = Boolean(initial?.cert_number);
  const [form, setForm] = useState({
    cert_number: initial?.cert_number || "",
    certificate_type_code: initial?.certificate_type_code || "",
    collector_full_name: initial?.collector_full_name || "",
    collector_nickname: initial?.collector_nickname || "",
    display_kind: initial?.display_kind || "full",
    album: initial?.album || "",
    completion_year: initial?.completion_year || "",
    inspected_on: initial?.inspected_on ? initial.inspected_on.substring(0, 10) : new Date().toISOString().substring(0, 10),
    notes: initial?.notes || "",
    show_socials: initial?.show_socials || false,
    instagram: initial?.instagram || "",
    tiktok: initial?.tiktok || "",
    twitter: initial?.twitter || "",
    generate_claim_code: true,
  });
  const [busy, setBusy] = useState(false);

  const activeTypes = (certTypes || []).filter((t) => t.active);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        cert_number: form.cert_number || null,
        completion_year: form.completion_year ? Number(form.completion_year) : null,
        certificate_type_code: form.certificate_type_code || null,
      };
      let response;
      if (isEdit) {
        // Editing never regenerates a claim code. That's a distinct action.
        delete payload.generate_claim_code;
        delete payload.certificate_type_code;
        const r = await api.patch(`/admin/certificates/${initial.cert_number}`, payload);
        response = r.data;
      } else {
        const r = await api.post("/admin/certificates", payload);
        response = r.data;
      }
      toast.success(isEdit ? "Certificate updated" : "Certificate created");
      onSaved(response);
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title={isEdit ? `Edit ${initial.cert_number}` : "New certificate"}>
      <form onSubmit={submit} className="space-y-4" data-testid="cert-form">
        {!isEdit && (
          <>
            <div>
              <div className="kicker kicker-black text-[11px] mb-2">Certificate type (leave blank to use album default)</div>
              <select
                className="gm-field"
                value={form.certificate_type_code}
                onChange={(e) => setForm({ ...form, certificate_type_code: e.target.value })}
                data-testid="cf-type"
              >
                <option value="">— Use album default —</option>
                {activeTypes.map((t) => (
                  <option key={t.id} value={t.code}>
                    {t.code} · {t.display_name} ({t.number_prefix}-…)
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-[var(--smoke)] mt-1">
                Deactivated types are hidden. Existing certificates keep their numbers even if a type is later deactivated.
              </p>
            </div>
            <TextField
              label="Certificate number (leave blank to auto-generate from the selected type)"
              value={form.cert_number}
              onChange={(v) => setForm({ ...form, cert_number: v })}
              testid="cf-number"
            />
            <label className="flex items-center gap-3 text-sm rounded-xl border-2 border-[var(--line)] p-3 bg-[var(--panini-yellow-soft)]/40">
              <input
                type="checkbox"
                checked={form.generate_claim_code}
                onChange={(e) => setForm({ ...form, generate_claim_code: e.target.checked })}
                data-testid="cf-generate-claim"
              />
              <span>
                <span className="font-bold">Generate a one-time claim code</span>
                <span className="block text-[var(--smoke)] text-xs">
                  A 16-character code the collector uses to claim ownership. Shown once, never stored in plaintext.
                </span>
              </span>
            </label>
          </>
        )}
        <TextField label="Collector full name" required value={form.collector_full_name} onChange={(v) => setForm({ ...form, collector_full_name: v })} testid="cf-fullname" />
        <TextField label="Collector nickname (optional)" value={form.collector_nickname} onChange={(v) => setForm({ ...form, collector_nickname: v })} testid="cf-nickname" />
        <div>
          <div className="kicker kicker-black text-[11px] mb-2">Public display name</div>
          <select className="gm-field" value={form.display_kind} onChange={(e) => setForm({ ...form, display_kind: e.target.value })} data-testid="cf-display">
            <option value="full">Full name</option>
            <option value="first">First name only</option>
            <option value="initials">Initials</option>
            <option value="nickname">Nickname</option>
            <option value="private">Private Collector</option>
          </select>
        </div>
        <TextField label="Album" required value={form.album} onChange={(v) => setForm({ ...form, album: v })} testid="cf-album" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Completion year (optional)" type="number" value={form.completion_year} onChange={(v) => setForm({ ...form, completion_year: v })} testid="cf-year" />
          <TextField label="Inspected on" required type="date" value={form.inspected_on} onChange={(v) => setForm({ ...form, inspected_on: v })} testid="cf-date" />
        </div>
        <div>
          <div className="kicker kicker-black text-[11px] mb-2">Notes (optional, shown publicly)</div>
          <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="gm-field" data-testid="cf-notes" />
        </div>

        <div className="rounded-xl border-2 border-[var(--line)] p-4">
          <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" checked={form.show_socials} onChange={(e) => setForm({ ...form, show_socials: e.target.checked })} data-testid="cf-showsocials" />
            Show social profiles publicly (collector has given permission)
          </label>
          {form.show_socials && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <TextField label="Instagram" value={form.instagram} onChange={(v) => setForm({ ...form, instagram: v })} testid="cf-ig" />
              <TextField label="TikTok" value={form.tiktok} onChange={(v) => setForm({ ...form, tiktok: v })} testid="cf-tt" />
              <TextField label="Twitter" value={form.twitter} onChange={(v) => setForm({ ...form, twitter: v })} testid="cf-tw" />
            </div>
          )}
        </div>

        <button type="submit" disabled={busy} data-testid="cf-submit" className="btn-red text-sm disabled:opacity-60">
          {busy ? "Saving…" : "Save certificate"}
        </button>
      </form>
    </Modal>
  );
}

// ---------- Contacts / Customers / Settings ----------
function ContactsAdmin() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/contact").then((r) => setRows(r.data)); }, []);
  if (!rows) return <div className="text-[var(--smoke)]">Loading…</div>;
  if (rows.length === 0) return <div className="text-[var(--smoke)]">No contact submissions yet.</div>;
  return (
    <div className="space-y-4">
      {rows.map((c) => (
        <div key={c.id} className="card-fun p-5" data-testid={`contact-${c.id}`}>
          <div className="flex justify-between text-sm">
            <div className="font-display text-xl">{c.full_name}</div>
            <div className="text-[var(--smoke)]">{new Date(c.created_at).toLocaleString()}</div>
          </div>
          <div className="text-[var(--smoke)] text-sm mt-1">
            {c.email}{c.phone ? ` · ${c.phone}` : ""}{c.subject ? ` · ${c.subject}` : ""}
          </div>
          <div className="mt-3 text-[var(--ink)] whitespace-pre-wrap text-sm">{c.message}</div>
        </div>
      ))}
    </div>
  );
}

function CollectorsAdmin() {
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("");
  const [assigningId, setAssigningId] = useState(null);

  const load = () => api.get("/admin/collectors").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const assignId = async (collectorId, name) => {
    if (!window.confirm(
      `Issue a permanent Public Collector ID to ${name}?\n\n` +
      `This is irreversible. The ID will be sequential (GM-Cxxxxxx), permanent, ` +
      `and cannot be reused or changed.`
    )) return;
    setAssigningId(collectorId);
    try {
      const r = await api.post(`/admin/collectors/${collectorId}/assign-public-collector-id`);
      toast.success(`Assigned ${r.data.public_collector_id} to ${name}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAssigningId(null);
    }
  };

  if (!rows) return <div className="text-[var(--smoke)]">Loading…</div>;
  if (rows.length === 0) return <div className="text-[var(--smoke)]">No collectors on file yet.</div>;

  const f = filter.toLowerCase();
  const shown = f
    ? rows.filter((r) => (r.full_name || "").toLowerCase().includes(f)
        || (r.normalized_email || "").includes(f)
        || (r.state || "").toLowerCase().includes(f)
        || (r.public_collector_id || "").toLowerCase().includes(f))
    : rows;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="kicker kicker-red">All collectors ({rows.length})</div>
        <input
          data-testid="collectors-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by name, email, state, Public Collector ID…"
          className="gm-field max-w-xs"
        />
      </div>
      <div className="rounded-2xl border-2 border-[var(--ink)] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[var(--smoke)] text-xs uppercase tracking-widest bg-[var(--panini-yellow-soft)]">
            <tr>
              <th className="text-left px-4 py-3">Public Collector ID</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">State</th>
              <th className="text-left px-4 py-3">Activity</th>
              <th className="text-left px-4 py-3">Source</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => (
              <tr key={p.id} className="border-t border-[var(--line)] hover:bg-[var(--panini-yellow-soft)]" data-testid={`collector-${p.id}`}>
                <td className="px-4 py-3">
                  {p.public_collector_id ? (
                    <span
                      className="font-mono text-[var(--panini-red)] font-bold"
                      data-testid={`public-collector-id-${p.id}`}
                    >
                      {p.public_collector_id}
                    </span>
                  ) : (
                    <button
                      onClick={() => assignId(p.id, p.full_name || p.email || "collector")}
                      disabled={assigningId === p.id}
                      data-testid={`assign-public-collector-id-${p.id}`}
                      className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border-2 border-[var(--line)] hover:border-[var(--panini-red)] text-[var(--smoke)] hover:text-[var(--panini-red)] font-bold disabled:opacity-60"
                      title="Issue a permanent public Collector ID"
                    >
                      {assigningId === p.id ? "Assigning…" : "Assign ID"}
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 font-bold">{p.full_name || "—"}</td>
                <td className="px-4 py-3">{p.normalized_email || p.email}</td>
                <td className="px-4 py-3">{p.state || "—"}</td>
                <td className="px-4 py-3 text-[var(--smoke)]">
                  {(p.counts?.sticker_requests || 0)}R · {(p.counts?.rsvps || 0)}E · {(p.counts?.certificates || 0)}C
                </td>
                <td className="px-4 py-3 text-[var(--smoke)] text-xs">{(p.sources || []).join(", ") || p.source}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setOpenId(p.id)}
                    data-testid={`collector-open-${p.id}`}
                    className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--panini-yellow)] font-bold"
                  >Open</button>
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-[var(--smoke)]">No collectors match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {openId && <CollectorDrawer collectorId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function CollectorDrawer({ collectorId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/collectors/${collectorId}/timeline`).then((r) => setData(r.data));
  }, [collectorId]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex justify-end" data-testid="collector-drawer">
      <div className="w-full max-w-2xl bg-white border-l-2 border-[var(--ink)] h-full overflow-y-auto">
        <div className="sticky top-0 bg-[var(--ink)] text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[var(--panini-yellow)] border-2 border-white flex items-center justify-center">
              <UserIcon size={18} className="text-[var(--ink)]" />
            </div>
            <div>
              <div className="font-display text-lg">{data?.collector?.full_name || "Collector"}</div>
              <div className="text-xs text-white/70">{data?.collector?.normalized_email}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="collector-drawer-close" className="text-white/80 hover:text-white"><X size={18} /></button>
        </div>
        {!data ? (
          <div className="p-8 text-[var(--smoke)]">Loading…</div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info label="Phone" value={data.collector.phone || "—"} />
              <Info label="State / City" value={`${data.collector.state || "—"} / ${data.collector.city || "—"}`} />
              <Info label="Instagram" value={data.collector.instagram || "—"} />
              <Info label="TikTok" value={data.collector.tiktok || "—"} />
              <Info label="Source" value={data.collector.source} />
              <Info label="Marketing email" value={data.collector.marketing_consent_email ? "granted" : "no"} />
              <Info label="Marketing SMS" value={data.collector.marketing_consent_sms ? "granted" : "no"} />
              <Info label="Possible duplicate" value={data.collector.possible_duplicate_status || "none"} />
            </div>
            <div>
              <div className="kicker kicker-red text-[11px] mb-3">Timeline ({data.timeline.length})</div>
              {data.timeline.length === 0 && <div className="text-[var(--smoke)] text-sm">No activity yet.</div>}
              <ol className="border-l-2 border-[var(--ink)] pl-5 space-y-4">
                {data.timeline.map((t, i) => (
                  <li key={i} className="relative" data-testid={`timeline-row-${i}`}>
                    <span className="absolute -left-[27px] top-1 w-4 h-4 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-yellow)]" />
                    <div className="flex justify-between gap-3">
                      <div className="font-bold text-sm">{t.title}</div>
                      <div className="text-[11px] text-[var(--smoke)] whitespace-nowrap">{new Date(t.at).toLocaleString()}</div>
                    </div>
                    <div className="text-xs text-[var(--smoke)]">{t.detail} {t.status ? `· ${t.status}` : ""}</div>
                    <span className="inline-block mt-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--panini-yellow-soft)] text-[var(--ink)] font-bold">{t.kind}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AlbumsAdmin() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/albums").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("Delete this album? (Only allowed if no requests/certs reference it.)")) return;
    try { await api.delete(`/admin/albums/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  if (!rows) return <div className="text-[var(--smoke)]">Loading…</div>;
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="kicker kicker-red">All albums</div>
        <button onClick={() => setEditing({})} data-testid="album-new" className="btn-red text-sm py-2 px-4">
          <Plus size={14} /> New album
        </button>
      </div>
      <div className="rounded-2xl border-2 border-[var(--ink)] bg-white overflow-hidden">
        {rows.length === 0 && <div className="p-6 text-[var(--smoke)]">No albums yet.</div>}
        {rows.map((a) => (
          <div key={a.id} className="grid grid-cols-12 gap-4 items-center px-5 py-4 border-b border-[var(--line)] text-sm" data-testid={`album-row-${a.id}`}>
            <div className="col-span-12 md:col-span-4 font-display text-xl">{a.name}</div>
            <div className="col-span-6 md:col-span-2 text-[var(--smoke)] font-mono text-xs">{a.slug}</div>
            <div className="col-span-6 md:col-span-2 text-[var(--smoke)]">{a.manufacturer} · {a.edition_year || "—"}</div>
            <div className="col-span-6 md:col-span-2 text-[var(--smoke)] font-mono text-xs">{a.request_number_prefix || "—"} / {a.certificate_number_prefix || "—"}</div>
            <div className="col-span-6 md:col-span-1">{a.active ? <span className="text-[var(--field-green)] font-bold text-xs">ACTIVE</span> : <span className="text-[var(--smoke)] text-xs">off</span>}</div>
            <div className="col-span-12 md:col-span-1 flex gap-2 justify-end">
              <button onClick={() => setEditing(a)} className="text-xs px-2 py-1.5 rounded-full border-2 border-[var(--line)] hover:border-[var(--ink)]" data-testid={`album-edit-${a.id}`}><Edit3 size={12} /></button>
              <button onClick={() => del(a.id)} className="text-xs px-2 py-1.5 rounded-full border-2 border-[var(--line)] hover:border-[var(--panini-red)] text-[var(--panini-red)]" data-testid={`album-del-${a.id}`}><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <AlbumForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function AlbumForm({ initial, onClose, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState({
    name: initial?.name || "",
    slug: initial?.slug || "",
    manufacturer: initial?.manufacturer || "Panini",
    competition: initial?.competition || "",
    edition_year: initial?.edition_year || "",
    total_stickers: initial?.total_stickers || "",
    active: initial?.active ?? true,
    request_enabled: initial?.request_enabled ?? true,
    certification_enabled: initial?.certification_enabled ?? true,
    request_number_prefix: initial?.request_number_prefix || "",
    certificate_number_prefix: initial?.certificate_number_prefix || "",
    cover_image_url: initial?.cover_image_url || "",
    description: initial?.description || "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        edition_year: form.edition_year ? Number(form.edition_year) : null,
        total_stickers: form.total_stickers ? Number(form.total_stickers) : null,
      };
      if (isEdit) await api.patch(`/admin/albums/${initial.id}`, payload);
      else await api.post("/admin/albums", payload);
      toast.success(isEdit ? "Album updated" : "Album created");
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title={isEdit ? "Edit album" : "New album"}>
      <form onSubmit={submit} className="space-y-4" data-testid="album-form">
        <TextField label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="af-name" />
        <TextField label="Slug (used in URLs, kebab-case)" required value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testid="af-slug" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Manufacturer" value={form.manufacturer} onChange={(v) => setForm({ ...form, manufacturer: v })} testid="af-manu" />
          <TextField label="Competition" value={form.competition} onChange={(v) => setForm({ ...form, competition: v })} testid="af-comp" />
          <TextField label="Edition year" type="number" value={form.edition_year} onChange={(v) => setForm({ ...form, edition_year: v })} testid="af-year" />
          <TextField label="Total stickers" type="number" value={form.total_stickers} onChange={(v) => setForm({ ...form, total_stickers: v })} testid="af-total" />
          <TextField label="Request number prefix (e.g. MSR-COPA)" value={form.request_number_prefix} onChange={(v) => setForm({ ...form, request_number_prefix: v })} testid="af-rpref" />
          <TextField label="Certificate prefix (e.g. GM-COPA-2027)" value={form.certificate_number_prefix} onChange={(v) => setForm({ ...form, certificate_number_prefix: v })} testid="af-cpref" />
        </div>
        <TextField label="Cover image URL" value={form.cover_image_url} onChange={(v) => setForm({ ...form, cover_image_url: v })} testid="af-cover" />
        <div>
          <div className="kicker kicker-black text-[11px] mb-2">Description</div>
          <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="gm-field" data-testid="af-desc" />
        </div>
        <div className="flex flex-wrap gap-6 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.request_enabled} onChange={(e) => setForm({ ...form, request_enabled: e.target.checked })} /> Request enabled</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.certification_enabled} onChange={(e) => setForm({ ...form, certification_enabled: e.target.checked })} /> Certification enabled</label>
        </div>
        <button type="submit" disabled={busy} data-testid="af-submit" className="btn-red text-sm disabled:opacity-60">
          {busy ? "Saving…" : "Save album"}
        </button>
      </form>
    </Modal>
  );
}

function AuditLogAdmin() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/audit-log").then((r) => setRows(r.data)); }, []);
  if (!rows) return <div className="text-[var(--smoke)]">Loading…</div>;
  if (rows.length === 0) return <div className="text-[var(--smoke)]">No audit entries yet.</div>;
  return (
    <div className="rounded-2xl border-2 border-[var(--ink)] bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[var(--smoke)] text-xs uppercase tracking-widest bg-[var(--panini-yellow-soft)]">
          <tr>
            <th className="text-left px-4 py-3">When</th>
            <th className="text-left px-4 py-3">Action</th>
            <th className="text-left px-4 py-3">Entity</th>
            <th className="text-left px-4 py-3">ID</th>
            <th className="text-left px-4 py-3">IP</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-[var(--line)] hover:bg-[var(--panini-yellow-soft)]" data-testid={`audit-${r.id}`}>
              <td className="px-4 py-3 text-[var(--smoke)] whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
              <td className="px-4 py-3 font-mono text-xs font-bold text-[var(--panini-red)]">{r.action}</td>
              <td className="px-4 py-3">{r.entity_type}</td>
              <td className="px-4 py-3 font-mono text-xs">{r.entity_id}</td>
              <td className="px-4 py-3 text-[var(--smoke)] text-xs">{r.ip_address || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SettingsAdmin() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data || {})); }, []);

  const set = (k) => (v) => setS((cur) => ({ ...(cur || {}), [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        // Legacy / core
        certificate_price_usd:
          s.certificate_price_usd !== undefined && s.certificate_price_usd !== ""
            ? Number(s.certificate_price_usd)
            : null,
        disclaimer_footer: s.disclaimer_footer ?? "",
        disclaimer_certificate: s.disclaimer_certificate ?? "",
        contact_email: s.contact_email ?? "",
        contact_phone: s.contact_phone ?? "",
        // Branding
        business_name: s.business_name ?? "",
        logo_url: s.logo_url ?? "",
        hero_image_url: s.hero_image_url ?? "",
        favicon_url: s.favicon_url ?? "",
        og_image_url: s.og_image_url ?? "",
        // Location & hours
        business_address: s.business_address ?? "",
        business_hours: s.business_hours ?? "",
        google_maps_url: s.google_maps_url ?? "",
        // Phase 3A.1 — Pickup location (used by fulfillment UI + reminder emails).
        pickup_location_name: s.pickup_location_name ?? "",
        pickup_location_address: s.pickup_location_address ?? "",
        pickup_location_hours: s.pickup_location_hours ?? "",
        pickup_location_instructions: s.pickup_location_instructions ?? "",
        // Social
        whatsapp_url: s.whatsapp_url ?? "",
        instagram_url: s.instagram_url ?? "",
        tiktok_url: s.tiktok_url ?? "",
        facebook_url: s.facebook_url ?? "",
        youtube_url: s.youtube_url ?? "",
        // Commerce
        shopify_store_url: s.shopify_store_url ?? "",
        // Zelle (Iter C/D)
        zelle_recipient_name: s.zelle_recipient_name ?? "",
        zelle_recipient_email: s.zelle_recipient_email ?? "",
        zelle_recipient_phone: s.zelle_recipient_phone ?? "",
        zelle_memo_hint: s.zelle_memo_hint ?? "",
        // Copy
        hero_headline: s.hero_headline ?? "",
        hero_subheadline: s.hero_subheadline ?? "",
        newsletter_text: s.newsletter_text ?? "",
        footer_copyright: s.footer_copyright ?? "",
        announcement_bar: s.announcement_bar ?? "",
        // Legal
        legal_jurisdiction: s.legal_jurisdiction ?? "",
        // Iter O · Notifications — legacy singular (kept for read-only compatibility)
        notification_email: s.notification_email ?? "",
        notification_email_enabled:
          s.notification_email_enabled === undefined ? true : !!s.notification_email_enabled,
        notification_whatsapp: s.notification_whatsapp ?? "",
        notification_whatsapp_enabled:
          s.notification_whatsapp_enabled === undefined ? true : !!s.notification_whatsapp_enabled,
        // Iter P · Multi-recipient. Fall back to the singular field (as a 1-list)
        // when the admin hasn't upgraded yet — the loader on the backend does the
        // same normalisation so front + back stay in lock-step.
        notification_emails: Array.isArray(s.notification_emails) && s.notification_emails.length
          ? s.notification_emails
          : (s.notification_email ? [s.notification_email] : []),
        notification_whatsapp_numbers: Array.isArray(s.notification_whatsapp_numbers) && s.notification_whatsapp_numbers.length
          ? s.notification_whatsapp_numbers
          : (s.notification_whatsapp ? [s.notification_whatsapp] : []),
      };
      // Strip null (price) so the PATCH doesn't overwrite when left empty
      Object.keys(payload).forEach((k) => payload[k] === null && delete payload[k]);
      await api.patch("/admin/settings", payload);
      toast.success("Website Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  if (!s) return <div className="text-[var(--smoke)]">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="settings-panel">
      <div>
        <div className="kicker kicker-red">Website Settings</div>
        <h2 className="font-display text-2xl md:text-3xl mt-1">Edit content without touching code.</h2>
        <p className="text-[var(--smoke)] text-sm mt-2 max-w-2xl">
          Values below flow through the whole public site — header, hero, footer, contact, legal pages,
          social icons, and SEO. Leave a field empty to fall back to the built-in default; blank social URLs
          are automatically hidden.
        </p>
      </div>

      <SettingsGroup title="Business identity" accent="yellow">
        <TextField label="Business name" value={s.business_name || ""} onChange={set("business_name")} testid="s-business-name" />
        <TextField label="Logo URL" value={s.logo_url || ""} onChange={set("logo_url")} testid="s-logo-url" placeholder="https://…/logo.png (transparent PNG or SVG)" />
        <TextField label="Favicon URL" value={s.favicon_url || ""} onChange={set("favicon_url")} testid="s-favicon-url" placeholder="https://…/favicon.png (32x32 or SVG)" />
        <TextField label="Open Graph image URL" value={s.og_image_url || ""} onChange={set("og_image_url")} testid="s-og-image-url" placeholder="https://…/og-image.jpg (1200x630)" />
        <TextField label="Hero image URL (Home + About)" value={s.hero_image_url || ""} onChange={set("hero_image_url")} testid="s-hero-image-url" placeholder="https://…/gil-portrait.jpg (paste your photo URL — used on Home hero and About page)" />
      </SettingsGroup>

      <SettingsGroup title="Contact & location" accent="green">
        <TextField label="Business address" value={s.business_address || ""} onChange={set("business_address")} testid="s-business-address" placeholder="City, State" />
        <TextField label="Business hours" value={s.business_hours || ""} onChange={set("business_hours")} testid="s-business-hours" placeholder="By appointment · trading events posted on Events" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Email" value={s.contact_email || ""} onChange={set("contact_email")} testid="s-email" placeholder="contact@…" />
          <TextField label="Phone" value={s.contact_phone || ""} onChange={set("contact_phone")} testid="s-phone" placeholder="+1 …" />
        </div>
        <TextField label="Google Maps URL (paste a Maps share or embed link)" value={s.google_maps_url || ""} onChange={set("google_maps_url")} testid="s-maps-url" placeholder="https://maps.google.com/…" />
      </SettingsGroup>

      {/* Phase 3A.1 — Pickup location used in fulfillment admin + reminder emails */}
      <SettingsGroup title="Pickup location (fulfillment)" accent="yellow">
        <p className="text-[var(--smoke)] text-xs">
          Surfaced in the admin fulfillment center for pickup orders and included
          in customer emails so buyers know exactly where to collect. Leave any
          field blank if you don&apos;t use it.
        </p>
        <TextField label="Location name" value={s.pickup_location_name || ""} onChange={set("pickup_location_name")} testid="s-pickup-name" placeholder="Your Phone Plug · Miami Gardens" />
        <TextField label="Pickup address" value={s.pickup_location_address || ""} onChange={set("pickup_location_address")} testid="s-pickup-address" placeholder="20719 NW 2nd Ave, Miami Gardens, FL 33169" />
        <TextField label="Pickup hours" value={s.pickup_location_hours || ""} onChange={set("pickup_location_hours")} testid="s-pickup-hours" placeholder="Mon–Sat 11am–7pm · Sun by appointment" />
        <TextField label="Pickup instructions (optional)" value={s.pickup_location_instructions || ""} onChange={set("pickup_location_instructions")} testid="s-pickup-instructions" placeholder="Ask for Gil at the counter." />
      </SettingsGroup>

      <SettingsGroup title="Social media (blank = hidden)" accent="red">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Instagram URL" value={s.instagram_url || ""} onChange={set("instagram_url")} testid="s-instagram" placeholder="https://instagram.com/…" />
          <TextField label="TikTok URL" value={s.tiktok_url || ""} onChange={set("tiktok_url")} testid="s-tiktok" placeholder="https://www.tiktok.com/@…" />
          <TextField label="Facebook URL" value={s.facebook_url || ""} onChange={set("facebook_url")} testid="s-facebook" placeholder="https://facebook.com/…" />
          <TextField label="YouTube URL" value={s.youtube_url || ""} onChange={set("youtube_url")} testid="s-youtube" placeholder="https://youtube.com/@…" />
          <TextField label="WhatsApp URL" value={s.whatsapp_url || ""} onChange={set("whatsapp_url")} testid="s-whatsapp" placeholder="https://wa.me/1…" />
        </div>
      </SettingsGroup>

      <SettingsGroup title="Commerce" accent="yellow">
        <TextField label="Shopify storefront URL" value={s.shopify_store_url || ""} onChange={set("shopify_store_url")} testid="s-shopify-url" placeholder="https://shop.collectorup.com" />
        <p className="text-[var(--smoke)] text-xs">
          The public site currently uses <code>REACT_APP_SHOPIFY_STOREFRONT_URL</code> from the frontend
          env. This field is a copy-of-record for the admin — set it once you also switch the env to
          the same value so both stay in sync.
        </p>
      </SettingsGroup>

      <SettingsGroup title="Zelle payments (customer payment page)" accent="red">
        <TextField label="Zelle recipient name" value={s.zelle_recipient_name || ""} onChange={set("zelle_recipient_name")} testid="s-zelle-name" placeholder="Gil Maakaroun" />
        <TextField label="Zelle email (recipient)" value={s.zelle_recipient_email || ""} onChange={set("zelle_recipient_email")} testid="s-zelle-email" placeholder="zelle@gilmaakaroun.com" />
        <TextField label="Zelle phone (recipient)" value={s.zelle_recipient_phone || ""} onChange={set("zelle_recipient_phone")} testid="s-zelle-phone" placeholder="+1-305-555-0123" />
        <TextField label="Memo hint (shown to customers)" value={s.zelle_memo_hint || ""} onChange={set("zelle_memo_hint")} testid="s-zelle-memo" placeholder="Please include your order ID in the memo." />
        <p className="text-[var(--smoke)] text-xs">
          At least one of Zelle email or phone must be set for the QR code + copy chips to render on the customer payment page. Leave blank to fall back to the &quot;contact us&quot; message.
        </p>
      </SettingsGroup>

      <SettingsGroup title="Homepage & site copy" accent="green">
        <TextField label="Hero headline" value={s.hero_headline || ""} onChange={set("hero_headline")} testid="s-hero-headline" placeholder="The home for Panini collectors." />
        <LongField label="Hero sub-headline" value={s.hero_subheadline || ""} onChange={set("hero_subheadline")} testid="s-hero-subheadline" rows={3} />
        <LongField label="Newsletter description" value={s.newsletter_text || ""} onChange={set("newsletter_text")} testid="s-newsletter-text" rows={3} />
        <TextField label="Footer copyright line (year prepended automatically)" value={s.footer_copyright || ""} onChange={set("footer_copyright")} testid="s-footer-copyright" placeholder="Gil Maakaroun. Made for collectors." />
        <TextField label="Announcement bar (leave empty to hide)" value={s.announcement_bar || ""} onChange={set("announcement_bar")} testid="s-announcement-bar" placeholder="Ex: Free local pickup this Saturday!" />
      </SettingsGroup>

      <SettingsGroup title="Legal & disclaimers" accent="black">
        <LongField label="Independence disclaimer (footer + legal pages)" value={s.disclaimer_footer || ""} onChange={set("disclaimer_footer")} testid="s-footer-disclaimer" rows={3} />
        <LongField label="Certificate-page disclaimer" value={s.disclaimer_certificate || ""} onChange={set("disclaimer_certificate")} testid="s-cert-disclaimer" rows={2} />
        <TextField label="Legal jurisdiction (Terms §11)" value={s.legal_jurisdiction || ""} onChange={set("legal_jurisdiction")} testid="s-legal-jurisdiction" placeholder="the Commonwealth of Massachusetts, United States" />
        <TextField label="Certificate price (USD)" type="number" value={s.certificate_price_usd ?? ""} onChange={set("certificate_price_usd")} testid="s-price" />
      </SettingsGroup>

      {/* Iter Q — Notifications section extracted to its own file to keep this
          component under the 700-line guideline. All test IDs and behaviour
          preserved verbatim. */}
      <SettingsNotificationsSection s={s} set={set} />

      <div className="sticky bottom-4 bg-[var(--panini-yellow)] border-2 border-[var(--ink)] rounded-2xl p-4 flex items-center justify-between shadow-[6px_6px_0_var(--ink)] z-10">
        <div className="text-sm font-bold text-[var(--ink)]">
          Save to update the public site immediately.
        </div>
        <button
          onClick={save}
          disabled={busy}
          data-testid="s-save"
          className="btn-red disabled:opacity-60"
        >
          {busy ? "Saving…" : "Save Website Settings"}
        </button>
      </div>
    </div>
  );
}

function SettingsGroup({ title, accent, children }) {
  const accentBar = {
    yellow: "bg-[var(--panini-yellow)]",
    red: "bg-[var(--panini-red)]",
    green: "bg-[var(--field-green)]",
    black: "bg-[var(--ink)]",
  }[accent] || "bg-[var(--panini-yellow)]";
  return (
    <section className="rounded-2xl border-2 border-[var(--ink)] bg-white overflow-hidden" data-testid={`settings-group-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className={`h-2 ${accentBar}`} />
      <div className="p-5 md:p-6">
        <h3 className="font-display text-xl">{title}</h3>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </section>
  );
}

function LongField({ label, value, onChange, testid, rows = 3 }) {
  return (
    <label className="block">
      <div className="kicker kicker-black text-[11px] mb-2">{label}</div>
      <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className="gm-field" data-testid={testid} />
    </label>
  );
}

// ---------- Small helpers ----------
function TextField({ label, value, onChange, required, type = "text", testid, placeholder }) {
  return (
    <label className="block">
      <div className="kicker kicker-black text-[11px] mb-2">{label} {required && <span className="text-[var(--panini-red)]">*</span>}</div>
      <input type={type} required={required} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="gm-field" data-testid={testid} placeholder={placeholder} />
    </label>
  );
}

// Iter Q — helpers extracted to SettingsNotificationsSection.jsx. Kept as
// local re-export aliases so any legacy call sites inside this file still work.
const ToggleField = SNSToggleField;
const RecipientListField = SNSRecipientListField;

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="admin-modal">
      <div className="w-full max-w-2xl bg-white border-2 border-[var(--ink)] rounded-2xl my-8 shadow-[8px_8px_0_var(--ink)]">
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-[var(--ink)]">
          <div className="font-display text-2xl">{title}</div>
          <button onClick={onClose} className="text-[var(--ink)]/70 hover:text-[var(--ink)]" data-testid="modal-close"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function toLocalInput(iso) {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
